/**
 * journey-pages.tsx — the JOURNEY pages adapter for JourneyFlowBuilder.
 *
 * The builder no longer knows what a page is; the host supplies a
 * QFPagesAdapter. The Journey Designer supplies
 * this: the page-type registry from nj-model, a miniature that renders the
 * document's real page, and — instead of a modal — a callback that switches the
 * designer to its own Pages view, which is where journey pages are built.
 *
 * Flow steps address a page by its INDEX in `doc.pages`, stringified, because
 * that is what the journey document has always stored. `pageKey` / `pageIdx`
 * are the only two places that knows.
 */
import type { QFPagesAdapter } from "./jf-model";
import { REG, regOf, stepNavFor, type Doc, type NJPage } from "../nj-model";

export const pageKey = (idx: number): string => String(idx);
export const pageIdx = (key: string): number => {
  const n = Number(key);
  return Number.isFinite(n) ? n : -1;
};

/** A service the journey can offer, as the Pages view knows it. The host passes
 *  the real catalogue (getServiceCatalogue / catServices in NewJourneys) — the
 *  miniature must not keep its own copy, or the two views can disagree about
 *  what a page offers. */
export interface MiniService { id: string; name: string; wait: string; appt?: boolean }

/** The journey's live setup values, so a flow miniature shows the SAME thing as
 *  the Pages view. Before this the miniature invented tickets ("Alumni
 *  Reception · $25"), a fixed contact form and a fixed event date, which meant
 *  the flow view contradicted Setup on every real journey. */
export interface MiniCtx {
  tickets?: { name: string; price: string }[];
  questions?: string[];
  when?: string;
  where?: string;
  /** The page's derived footer, so a miniature shows the button the customer
   *  will actually see instead of a per-id guess that predates step nav. */
  nav?: { label: string; postCommit: boolean };
}

interface MiniModel {
  rows: { n: string; m: string }[];
  fields: string[];
  stat: { big: string; sub: string } | null;
  summary: { k: string; v: string }[];
  stars: boolean;
  cta: string;
  ctaIsLink?: boolean;
  body: string[];
}

/** What this page actually shows — one description, rendered at any scale. */
export function miniModel(pg: NJPage, docType: string | undefined, services: MiniService[] = [], ctx: MiniCtx = {}): MiniModel {
  const id = pg.id;
  /* Resolve the page's OWN selection against the catalogue. Slicing by the
     COUNT of selected ids changed only how many rows appeared, never which. */
  const cat = services.filter((s) => (id === "apptsvc" ? !!s.appt : !s.appt));
  const picked = pg.svc ?? [];
  const rows =
    id === "services" || id === "apptsvc"
      ? (picked.length ? cat.filter((s) => picked.indexOf(s.id) > -1) : cat).slice(0, 3).map((s) => ({ n: s.name, m: s.wait }))
      : id === "tickets"
        ? (ctx.tickets ?? []).slice(0, 3).map((t) => ({ n: t.name, m: t.price }))
        : id === "exphub"
          ? [{ n: "ID card pickup", m: "~8 min" }, { n: "Holds & balances", m: "~12 min" }]
          : [];
  const attached = (ctx.questions ?? []).slice(0, 3);
  const fields =
    id === "contact" ? ["First name", "Last name", "Email address", "Mobile number", ...attached]
      : id === "register" ? ["First name", "Last name", "Email address", ...attached]
        : [];
  const stat =
    id === "wait" ? { big: "~15 min", sub: "3 people ahead of you" }
      : id === "waiting" ? { big: "#4", sub: "in line · about 12 min" }
        : id === "appttime" ? { big: "10:30", sub: "Tue 18 Aug · 45 min" }
          : null;
  /* A page carrying the Event details block shows the event's REAL date and
     venue — the same values the Pages view reads from the record. */
  const hasEventDetails = (pg.content ?? []).some((b) => b.type === "eventdetails");
  const summary =
    id === "apptconfirm"
      ? [{ k: "Service", v: "Degree Planning Session" }, { k: "Advisor", v: "Dana Whitfield" }, { k: "Time", v: "Tue 18 Aug · 10:30" }]
      : hasEventDetails || id === "eventlanding"
        ? ([ctx.when ? { k: "When", v: ctx.when } : null, ctx.where ? { k: "Where", v: ctx.where } : null].filter(Boolean) as { k: string; v: string }[])
        : [];
  /* The contact page is shared by all three verticals, so its button follows
     the page's own label first and the journey type second. */
  const ctaByType: Record<string, string> = { Queue: "Join the line", Appointment: "Confirm booking", Event: "Save my spot" };
  const perId: Record<string, string> = {
    wait: ctaByType[docType ?? ""] ?? "Continue",
    contact: ctaByType[docType ?? ""] ?? "Continue",
    csat: "Take the survey",
    apptconfirm: "Add to calendar",
    appttime: "Confirm this time",
    eventlanding: "Register — it's free",
    register: "Save my spot",
    tickets: "Checkout",
  };
  /* The footer's forward button wins where the host supplied it. A post-commit
     page has no footer, so it falls back to its own action, which the mini draws
     as a link. */
  const cta = ctx.nav && !ctx.nav.postCommit ? ctx.nav.label : ((pg.cta as string | undefined) || perId[id] || "");
  const body = (pg.content ?? [])
    .filter((b) => b.type === "text" || b.type === "notice")
    .map((b) => String((b as { text?: string }).text ?? ""))
    .filter(Boolean)
    .slice(0, 2);
  return { rows, fields, stat, summary, stars: id === "csat", cta, body, ctaIsLink: !!ctx.nav?.postCommit };
}

/** The page, laid out at a logical 375px and scaled — the same approach as the
 *  queue's QPageMini, which renders the shared page renderer scaled down. */
export function NJPageMini({ page, docType, scale = 0.34, height, services, ctx }: { page: NJPage; docType?: string; scale?: number; height?: number; services?: MiniService[]; ctx?: MiniCtx }) {
  const m = miniModel(page, docType, services ?? [], ctx ?? {});
  const w = Math.round(375 * scale);
  const h = height ?? Math.round(375 * scale);
  return (
    <div style={{ width: w, height: h, boxSizing: "border-box", overflow: "hidden", borderRadius: 10, border: "1px solid #E6E8EC", background: "#FFFFFF", flex: "none" }}>
      <div style={{ width: 375, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <div style={{ background: page.tint, padding: "16px 18px 14px" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#14131F", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{page.h || page.label}</div>
          {page.s ? <div style={{ fontSize: 13, color: "#4B5563", marginTop: 5, lineHeight: 1.4 }}>{page.s}</div> : null}
        </div>
        <div style={{ padding: "14px 18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
          {m.stat ? (
            <div style={{ textAlign: "center", padding: "6px 0" }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: "#14131F", letterSpacing: "-0.02em" }}>{m.stat.big}</div>
              <div style={{ fontSize: 13, color: "#697180", marginTop: 2 }}>{m.stat.sub}</div>
            </div>
          ) : null}
          {m.rows.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {m.rows.map((r) => (
                <div key={r.n} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #E6E8EC", borderRadius: 10, padding: "11px 12px" }}>
                  <span style={{ width: 30, height: 30, borderRadius: 8, background: page.tint, flex: "none" }} />
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "#14131F" }}>{r.n}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: page.color, background: page.tint, borderRadius: 999, padding: "3px 10px", flex: "none" }}>{r.m}</span>
                </div>
              ))}
            </div>
          ) : null}
          {m.fields.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {m.fields.map((fl) => (
                <div key={fl} style={{ border: "1px solid #D1D4DC", borderRadius: 9, padding: "11px 12px", fontSize: 13, color: "#B5BAC6" }}>{fl}</div>
              ))}
            </div>
          ) : null}
          {m.summary.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {m.summary.map((s) => (
                <div key={s.k} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5 }}>
                  <span style={{ color: "#697180" }}>{s.k}</span><b style={{ color: "#14131F" }}>{s.v}</b>
                </div>
              ))}
            </div>
          ) : null}
          {m.stars ? <div style={{ textAlign: "center", fontSize: 26, letterSpacing: 5, color: "#F5C61E" }}>★★★★★</div> : null}
          {m.body.map((tx, i) => (
            <div key={i} style={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.5 }}>{tx}</div>
          ))}
          {m.cta ? (
            m.ctaIsLink
              ? <div style={{ fontSize: 13.5, fontWeight: 700, color: page.color, textDecoration: "underline" }}>{m.cta}</div>
              : <div style={{ height: 42, borderRadius: 11, background: page.color, color: "#FFFFFF", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{m.cta}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The adapter. Closed over the document so the miniature can resolve a page
 *  index, and over `onOpenPage` so a page node routes to the Pages view. */
export function journeyPages(doc: Doc, onOpenPage: (idx: number) => void, services: MiniService[] = [], ctx: MiniCtx = {}): QFPagesAdapter {
  const at = (key: string): NJPage | null => doc.pages[pageIdx(key)] ?? null;
  return {
    order: REG.map((r) => r.id),
    meta: (key) => {
      const pg = at(key);
      if (pg) return { label: pg.label, sub: pg.core ? `Core: ${pg.core}` : "Content page — blocks only", color: pg.color, tint: pg.tint };
      const r = regOf(key);
      return { label: r.label, sub: r.desc, color: r.color, tint: r.tint };
    },
    defaultCfg: () => ({}),
    Mini: ({ page, scale }) => {
      const pg = at(page);
      if (!pg) return <div style={{ width: 128, height: 128, borderRadius: 10, border: "1px dashed #D1D4DC", background: "#FAFAFD" }} />;
      const nv = stepNavFor(doc, pageIdx(page));
      return <NJPageMini page={pg} docType={doc.type} scale={scale ?? 0.34} height={scale && scale > 0.4 ? 250 : 128} services={services} ctx={{ ...ctx, nav: { label: nv.label, postCommit: nv.postCommit } }} />;
    },
    hint: (key) => {
      const pg = at(key);
      if (!pg) return "This step points at a page that was deleted.";
      return pg.core
        ? `${pg.core} — built in the Pages view.`
        : "Content page — blocks only, built in the Pages view.";
    },
    /* No modal: journey pages are built in the designer's Pages view. */
    onOpenPage: (_stepId, key) => onOpenPage(pageIdx(key)),
  };
}
